import { useContext, useEffect, useState } from "react";
import { refreshContext } from "../contexts/refreshContext";
import { Accessibility } from "../models/Accessibility";
import { PropertyAccessibilityImages } from "../models/PropertyAccessibilityImages";
import {
  AccessibilityUnit,
} from "../models/AccessibilityUnits";
import { Property } from "../models/Property";
import {
  retrieveAccessibilityUnitsByTypeWithFields,
  listAccessibilityImagesByPropertyId,
  retrieveAccessibilityUnitsLevelsByUnitId,
  retrieveAccessibilityByUnitId, retrieveRelatedAccessibilityUnitsByUnit,
} from "../services/property.service";
import {replaceAccessibilitiesTitle} from "../utils/accessibilities";
import {A11yStandardType} from "../models/A11yStandard";

export const useFetchDetailedUnitData = (property: Property | undefined, unitItem: AccessibilityUnit, a11yStandard: A11yStandardType) => {

  const [loading, setLoading] = useState(false);
  const [refresh] = useContext(refreshContext);

  const [propertyItemAccessibilityUnits, setPropertyItemAccessibilityUnits] = useState<AccessibilityUnit[]>([]);
  const [itemAccessibilities, setItemAccessibilities] = useState<Accessibility[]>([]);
  const [propertyItemImages, setPropertyItemImages] = useState<PropertyAccessibilityImages[]>([]);
  const [unitItemFloors, setUnitItemFloors] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      // the accessibilities need the units, the images and the floors need nothing, so they load meanwhile
      const loadUnitsAndAccessibilities = async () => {
        const units = await getPropertyItemAccessibilityUnits()
        return {units, accessibilities: await getRoomAccessibilities(units)}
      }
      const [{units: preparedUnits, accessibilities: roomAccessibilities}, images] = await Promise.all([
        loadUnitsAndAccessibilities(),
        getImages(),
        getUnitLevels(),
      ])
      setItemAccessibilities(roomAccessibilities);
      setPropertyItemAccessibilityUnits(preparedUnits)
      setPropertyItemImages(images)

      setLoading(false)
    }
    fetchData().catch(console.error)
    return () => {
      setPropertyItemAccessibilityUnits([]);
      setItemAccessibilities([]);
      setPropertyItemImages([]);
      setUnitItemFloors("");
      setLoading(false)

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, unitItem, property])

  const getPropertyItemAccessibilityUnits = async () => {
    const [accessibilityUnits, relatedAccessibilityUnits] = property ? await Promise.all([
      retrieveAccessibilityUnitsByTypeWithFields(property.id, unitItem.type ,"id,name,accessibility_unit,title,related_to,is_accessible_unit"),
      retrieveRelatedAccessibilityUnitsByUnit(unitItem.id),
    ]) : [null, null];

    let preparedUnits: AccessibilityUnit[] = []
    if (accessibilityUnits && accessibilityUnits.results) {
      preparedUnits = accessibilityUnits.results
      if (relatedAccessibilityUnits && relatedAccessibilityUnits?.results.length > 0) {
        preparedUnits = preparedUnits.concat(relatedAccessibilityUnits.results)
      }
    }
    return preparedUnits
  }

  const getRoomAccessibilities = async (preparedUnits: AccessibilityUnit[]) => {
    // the accessibilities of the unit, then of the units related to it, all requested at once
    const relatedAccessibilityUnits = preparedUnits.filter((item) => (
      item.related_to && item.related_to.some((related) => related === unitItem.id)
    ))
    const responses = await Promise.all(
      [unitItem, ...relatedAccessibilityUnits].map(({id}) => retrieveAccessibilityByUnitId(id))
    )
    const roomAccessibilities = responses.flatMap((response) => response.results)

    const filteredOnlyAbsent = roomAccessibilities.filter(
      (access) => access.is_absent === false
    );

    return replaceAccessibilitiesTitle(filteredOnlyAbsent, preparedUnits, a11yStandard)
  }

  const getImages = async () => {
    const images = property ? (
      await listAccessibilityImagesByPropertyId(property.id)
    ) : null;
    return images ? images.results : []
  }

  const getUnitLevels = async () => {
    const floors = unitItem ? (
      await retrieveAccessibilityUnitsLevelsByUnitId(unitItem.id)
    ) : null;
    if (floors) {
      if (floors.results && floors.results.length > 1) {
        let minFloor = floors.results[0].level;
        let maxFloor = floors.results[0].level;
        floors.results.forEach((floorObj) => {
          if (floorObj.level < minFloor) {
            minFloor = floorObj.level;
          }
          if (floorObj.level > maxFloor) {
            maxFloor = floorObj.level;
          }
        })
        if (minFloor === maxFloor) {
          setUnitItemFloors(floors.results[0].level.toString());
        } else {
          setUnitItemFloors(`${minFloor} - ${maxFloor}`);
        }
      } else if (floors.results && floors.results.length === 1) {
        setUnitItemFloors(floors.results[0].level.toString());
      } else {
        setUnitItemFloors("");
      }
    }
  }

  return {
    loading,
    propertyItemAccessibilityUnits,
    itemAccessibilities,
    propertyItemImages, 
    unitItemFloors,
  };
};